import { BillingAddress } from 'Common/Models/Address/BillingAddress';
import { ShippingAddress } from 'Common/Models/Address/ShippingAddress';
import { Customer } from 'Common/Models/Customer';
import { ChevronRight } from 'lucide-react';
import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { UserProfileTypes } from 'Common/constants/UserProfileTypes';
import withMessages, { WithMessagesProps } from 'Common/withMessages';
import { WithTranslationProps } from 'Common/Components/Translation/WithTranslationProps';
import withNotifications, { WithNotificationsProps } from 'Common/withNotifications';
import { WithAuthProps } from 'Common/Components/Authentication/WithAuthProps';
import { NotificationType } from '../../Common/Components/Notifications/notificationTypes';
import { axios_helper_async } from 'helpers/Axios';
import { MessageType } from 'Common/Components/Messages/messageTypes';
import withTranslation from 'Common/withTranslation';
import withAuth from 'Common/Components/Authentication/withAuth';
import AccountInformationEditor from './AccountInformation/AccountInformationEditor';
import AccountPassword from './AccountInformation/AccountPassword';
import BillingAddressEditor from './Addresses/BillingAddressEditor';
import ShippingAddressEditor from './Addresses/ShippingAddressEditor';
import { OrderView } from 'Common/Models/OrderView';
import { WithCurrencyProps } from 'Common/Components/Currency/WithCurrencyProps';
import { withCurrency } from 'Common/Components/Currency/withCurrency';
import languages from 'Common/constants/languages';
import OrderHistory from './OrdersHistory/OrderHistory';
import { countriesTranslations } from 'Common/constants/countriesLocales';

const UserProfile: React.FC<WithTranslationProps & WithMessagesProps & WithNotificationsProps & WithAuthProps & WithCurrencyProps> = ({
  account,
  t,
  i18n,
  c,
  currency,
  messages,
  notifications
}) => {
  const initialCustomer: Customer = {
    createdAt: '',
    email: '',
    firstName: '',
    id: 0,
    isActive: false,
    isVerified: false,
    lastName: '',
    password: '',
    phone: '',
    updatedAt: ''
  }
  const initialBiilingAddress: BillingAddress = {
    taxId: '',
    customerId: Number(account.id),
    addressLine: '',
    city: '',
    company: '',
    country: '',
    // id: 0,
    phone: '',
    state: '',
    street: '',
    zipCode: '',
    // countryCode: ''
  }

  const initialShippingAddress: ShippingAddress = {
    customerId: Number(account.id),
    addressLine: '',
    city: '',
    company: '',
    country: '',
    // id: 0,
    phone: '',
    state: '',
    street: '',
    zipCode: '',
    // countryCode: ''
  }

  const [customer, setCustomer] = useState<Customer>(initialCustomer);
  const [billingAddress, setBillingAddress] = useState<BillingAddress>(initialBiilingAddress);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>(initialShippingAddress);
  const [orders, setOrders] = useState<OrderView[]>([]);
  const [activeTab, setActiveTab] = useState<UserProfileTypes>(UserProfileTypes.Account);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenAccountEditor, setIsOpenAccountEditor] = useState<boolean>(false);
  const [isOpenPasswordEditor, setIsOpenPasswordEditor] = useState<boolean>(false);
  const [isOpenBillingAddressEditor, setIsOpenBillingAddressEditor] = useState<boolean>(false);
  const [isOpenShippingAddressEditor, setIsOpenShippingAddressEditor] = useState<boolean>(false);
  const [isOrderOpen, setIsOrderOpen] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<number>(0);
  const [customerId, setCustomerId] = useState<number>(0);

  const handleGetUser = async () => {
    if (account.id) {
      try {
        setLoading(true);
        const body = {
          id: account.id
        }

        const response = await axios_helper_async<Customer>("/Account/Get-Customer-Information", 'GET', body);

        messages.setMessages(response.messages);
        notifications.setNotifications(response.notifications);

        if (response.isSuccess) {
          if (response.data) {
            setCustomer(response.data);
          }
        }
        else {
          messages.showMessage("UserProfile.Errors.GetCustomerInformation", MessageType.Error, "Error");
        }
      }
      catch (err: any) {
        notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
      }
      finally {
        setLoading(false);
      }
    }
  }

  const handleGetCustomerBillingAddress = async () => {
    if (account.id) {
      try {
        setLoading(true);
        const body = {
          id: account.id
        }

        const response = await axios_helper_async<BillingAddress>("/Account/Get-Customer-Billing-Address", 'GET', body);

        messages.setMessages(response.messages);
        notifications.setNotifications(response.notifications);

        if (response.isSuccess) {
          if (response.data) {
            setBillingAddress(response.data);
          }
        }
        else {
          messages.showMessage("UserProfile.Errors.GetCustomerBillingAddress", MessageType.Error, "Error");
        }
      }
      catch (err: any) {
        notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
      }
      finally {
        setLoading(false);
      }
    }
  }

  const handleGetCustomerShippingAddress = async () => {
    if (account.id) {
      try {
        setLoading(true);
        const body = {
          id: account.id
        }

        const response = await axios_helper_async<ShippingAddress>("/Account/Get-Customer-Shipping-Address", 'GET', body);

        messages.setMessages(response.messages);
        notifications.setNotifications(response.notifications);

        if (response.isSuccess) {
          if (response.data) {
            setShippingAddress(response.data);
          }
        }
        else {
          messages.showMessage("UserProfile.Errors.GetCustomerShippingAddress", MessageType.Error, "Error");
        }
      }
      catch (err: any) {
        notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
      }
      finally {
        setLoading(false);
      }
    }
  }

  const handleGetCustomerOrders = async () => {
    if (account.id) {
      try {
        setLoading(true);
        const body = {
          customerId: account.id
        }

        const response = await axios_helper_async<OrderView[]>("/Account/Get-Customer-Orders-By-CustomerId", 'GET', body);

        messages.setMessages(response.messages);
        notifications.setNotifications(response.notifications);

        if (response.isSuccess) {
          if (response.data) {
            setOrders(response.data);
          }
        }
        else {
          messages.showMessage("UserProfile.Errors.GetCustomerOrders", MessageType.Error, "Error");
        }
      }
      catch (err: any) {
        notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
      }
      finally {
        setLoading(false);
      }
    }
  }

  const handleOrderHistoryOnClose = () => {
    setOrderId(0);
    setCustomerId(0);
    setIsOrderOpen(false);
  }

  const handleOnClose = async () => {
    switch (activeTab) {
      case UserProfileTypes.Account:
        break;
      case UserProfileTypes.Addresses:
        break;
      case UserProfileTypes.OrderHistory:
        break;
    }
  }

  const hanldeCloseAccountEditor = () => {
    setIsOpenAccountEditor(false);
  }
  const hanldeClosePasswordEditor = () => {
    setIsOpenPasswordEditor(false);
  }
  const handleCloseBillingAddress = () => {
    setIsOpenBillingAddressEditor(false);
  }
  const handleCloseShippingAddress = () => {
    setIsOpenShippingAddressEditor(false);
  }
  const getData = async () => {
    switch (activeTab) {
      case UserProfileTypes.Account:
        await handleGetUser()
        break;
      case UserProfileTypes.Addresses:
        await handleGetCustomerBillingAddress();
        await handleGetCustomerShippingAddress();
        break;
      case UserProfileTypes.OrderHistory:
        await handleGetCustomerOrders();
        break;
    }
  }

  useEffect(() => {
    (async () => {
      await getData();
    })();

    if (activeTab !== UserProfileTypes.OrderHistory) {
      handleOrderHistoryOnClose();
    }

  }, [activeTab, account.id])

  useEffect(() => {
    if (!isOpenAccountEditor) {
      (async () => {
        await getData();
      })();
    }
  }, [isOpenAccountEditor])

  useEffect(() => {
    if (!isOpenBillingAddressEditor) {
      (async () => {
        await handleGetCustomerBillingAddress();
      })();
    }
  }, [isOpenBillingAddressEditor])

  useEffect(() => {
    if (!isOpenShippingAddressEditor) {
      (async () => {
        await handleGetCustomerShippingAddress();
      })();
    }
  }, [isOpenShippingAddressEditor])

  return (
    <div className="flex flex-col md:flex-row h-full w-full">
      <div className="w-full md:w-1/6 border-b md:border-r p-4">
        <div className="flex flex-col md:space-y-0">
          <button
            className={`w-full py-4 text-center transition-colors duration-300 
        ${activeTab === UserProfileTypes.Account
                ? 'bg-blue-500 text-white'
                : 'bg-white text-black hover:bg-gray-100 hover:text-blue-500'
              } border-b border-gray-300`}
            onClick={() => setActiveTab(UserProfileTypes.Account)}
          >
            {t('UserProfile.Account')}
          </button>

          <button
            className={`w-full py-4 text-center transition-colors duration-300 
        ${activeTab === UserProfileTypes.Addresses
                ? 'bg-blue-500 text-white'
                : 'bg-white text-black hover:bg-gray-100 hover:text-blue-500'
              } border-b border-gray-300`}
            onClick={() => setActiveTab(UserProfileTypes.Addresses)}
          >
            {t('UserProfile.Addresses')}
          </button>

          <button
            className={`w-full py-4 text-center transition-colors duration-300 
        ${activeTab === UserProfileTypes.OrderHistory
                ? 'bg-blue-500 text-white'
                : 'bg-white text-black hover:bg-gray-100 hover:text-blue-500'
              }`}
            onClick={() => setActiveTab(UserProfileTypes.OrderHistory)}
          >
            {t('UserProfile.OrderHistory')}
          </button>
        </div>
      </div>
      <div className="w-full md:w-5/6 p-4">
        {activeTab === UserProfileTypes.Account && (
          <div>
            <h2 className="text-2xl mb-4">{t("UserProfile.AccountEditor.Title")}</h2>

            {/* Si no está cargando, mostramos los datos */}
            {!loading ? (
              <>
                <table className="text-left">
                  <tbody>
                    <tr>
                      <td className="font-bold py-2 pr-4">{t("UserProfile.AccountEditor.FirstName")}:</td>
                      <td className="py-2">{customer.firstName ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold py-2 pr-4">{t("UserProfile.AccountEditor.LastName")}:</td>
                      <td className="py-2">{customer.lastName ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold py-2 pr-4">{t("UserProfile.AccountEditor.Email")}:</td>
                      <td className="py-2">{customer.email ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold py-2 pr-4">{t("UserProfile.AccountEditor.Phone")}:</td>
                      <td className="py-2">{customer.phone ?? "-"}</td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="text-center py-4">
                        <button className="btn btn-custom" onClick={() => setIsOpenAccountEditor(true)}>{t("General.Change")}</button>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2} className="border-t mt-4 pt-4"></td>
                    </tr>
                    <tr>
                      <td className="font-bold py-2 pr-4">{t("UserProfile.PasswordEditor.Title")}:</td>
                      <td className="flex py-2 gap-2">
                        <input
                          className="form-input bg-gray-100"
                          value="PasswordField"
                          type="password"
                          readOnly
                        />
                        <button className="btn btn-custom" onClick={() => setIsOpenPasswordEditor(true)}>{t("General.Change")}</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <AccountInformationEditor isOpen={isOpenAccountEditor} onClose={handleOnClose} customer={customer} close={hanldeCloseAccountEditor} />
                <AccountPassword isOpen={isOpenPasswordEditor} onClose={handleOnClose} id={customer.id} close={hanldeClosePasswordEditor} />
              </>
            ) : (
              // Si está cargando, mostramos el layout de carga
              <table className="text-left">
                <tbody>
                  {['First Name:', 'Last Name:', 'Email:', 'Phone:', 'Password:'].map((label, index) => (
                    <tr key={index}>
                      <td className="font-bold py-2 pr-4">{label}</td>
                      <td className="py-2 w-56">
                        <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {activeTab === UserProfileTypes.Addresses && (
          <div>
            <h2 className="text-2xl mb-4">{t("UserProfile.BillingAddressEditor.Title")}</h2>

            {/* Si no está cargando, mostramos los datos */}
            {!loading ? (
              <>
                <table className="text-left">
                  <tbody className=''>
                    <tr className="">
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.TaxId")}:</td>
                      <td className="py-0">{billingAddress.taxId ?? "-"}</td>
                    </tr>
                    <tr className="">
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Street")}:</td>
                      <td className="py-0">{billingAddress.street ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.AddressLine")}:</td>
                      <td className="py-0">{billingAddress.addressLine ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.City")}:</td>
                      <td className="py-0">{billingAddress.city ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.State")}:</td>
                      <td className="py-0">{billingAddress.state ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.ZipCode")}:</td>
                      <td className="py-0">{billingAddress.zipCode ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Country")}:</td>
                      <td className="py-0">{countriesTranslations.find(traslations => traslations.language === i18n.language)?.values.find(countries => countries.value === billingAddress.country)?.label}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Company")}:</td>
                      <td className="py-0">{billingAddress.company ?? "-"}</td>
                    </tr>
                    <tr>
                      <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Phone")}:</td>
                      <td className="py-0">{billingAddress.phone ?? "-"}</td>
                    </tr>
                  </tbody>
                </table>
                <BillingAddressEditor address={billingAddress} isOpen={isOpenBillingAddressEditor} onClose={handleOnClose} close={handleCloseBillingAddress}></BillingAddressEditor>
              </>
            ) : (
              // Animación de carga si está cargando
              <table className="text-left">
                <tbody>
                  {['TaxId', 'Street', 'AddressLine', 'City', 'State', 'ZipCode', 'Country', 'Company', 'Phone'].map((label, index) => (
                    <tr key={index}>
                      <td className="font-bold pr-4">{t(`UserProfile.BillingAddressEditor.${label}`)}:</td>
                      <td className="py-1 w-56">
                        <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!loading && <button className="btn btn-custom mt-4" onClick={() => setIsOpenBillingAddressEditor(true)}>{t("General.Change")}</button>}

            <div className="border-t mt-4 pt-4">
              <h2 className="text-2xl mb-4">{t("UserProfile.ShippingAddressEditor.Title")}</h2>

              {/* Si no está cargando, mostramos los datos */}
              {!loading ? (
                <>
                  <table className="text-left">
                    <tbody>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Street")}:</td>
                        <td className="py-0">{shippingAddress.street ?? "-"}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.AddressLine")}:</td>
                        <td className="py-0">{shippingAddress.addressLine ?? "-"}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.City")}:</td>
                        <td className="py-0">{shippingAddress.city ?? "-"}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.State")}:</td>
                        <td className="py-0">{shippingAddress.state ?? "-"}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.ZipCode")}:</td>
                        <td className="py-0">{shippingAddress.zipCode ?? "-"}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Country")}:</td>
                        <td className="py-0">{countriesTranslations.find(traslations => traslations.language === i18n.language)?.values.find(countries => countries.value === shippingAddress.country)?.label}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Company")}:</td>
                        <td className="py-0">{shippingAddress.company ?? "-"}</td>
                      </tr>
                      <tr>
                        <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Phone")}:</td>
                        <td className="py-0">{shippingAddress.phone ?? "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                  <ShippingAddressEditor address={shippingAddress} isOpen={isOpenShippingAddressEditor} onClose={handleOnClose} close={handleCloseShippingAddress}></ShippingAddressEditor>
                </>
              ) : (
                // Animación de carga si está cargando
                <table className="text-left">
                  <tbody>
                    {['Street', 'AddressLine', 'City', 'State', 'ZipCode', 'Country', 'Company', 'Phone'].map((label, index) => (
                      <tr key={index}>
                        <td className="font-bold pr-4">{t(`UserProfile.ShippingAddressEditor.${label}`)}:</td>
                        <td className="py-1 w-56">
                          <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {!loading && (
                <div className="flex space-x-4 mt-4">
                  <button className="btn btn-custom" onClick={() => setIsOpenShippingAddressEditor(true)}>{t("General.Change")}</button>
                </div>
              )}
            </div>
          </div>
        )}
        {activeTab === UserProfileTypes.OrderHistory && (
          <div>
            <h2 className="text-2xl mb-4">{t("UserProfile.Orders.Title")}</h2>

            {
              loading ?
                <>
                  {Array.from({ length: 1 }).map((_, index) => (
                    <div 
                      key={index}
                      className="flex flex-row w-full justify-between items-center bg-gray-100 p-4 mb-4 cursor-pointer hover:shadow-lg border border-gray-300 rounded-lg"
                    >
                      <div
                        className="flex flex-col md:flex-row justify-between items-start md:items-center w-full"     
                      >
                        {/* Columna 1: Order ID */}
                        <div className="flex-1 pr-4">
                          <p className="font-bold">{t("UserProfile.Orders.Id")}</p>
                          <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                        </div>

                        {/* Columna 2: Order Date */}
                        <div className="flex-1 px-4">
                          <p className="font-bold">{t("UserProfile.Orders.Date")}</p>
                          <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                        </div>

                        {/* Columna 3: Total */}
                        <div className="flex-1 px-4">
                          <p className="font-bold">{t("UserProfile.Orders.Total")} {'(USD)'}</p>
                          <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                        </div>

                        {/* Columna 4: Status */}
                        <div className="flex-1 px-4">
                          <p className="font-bold">{t("UserProfile.Orders.Status")}</p>
                          <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                        </div>
                      </div>
                      {/* Flecha para detalles */}
                      <div className="pl-4">
                        <ChevronRight className="text-custom-500" />
                      </div>
                    </div>
                  ))}
                </>
                :
                <>
                  {!isOrderOpen ? orders.map((order) => (
                    <div 
                      key={order.id} 
                      className="flex flex-row w-full justify-between items-center bg-gray-100 p-4 mb-4 cursor-pointer hover:shadow-lg border border-gray-300 rounded-lg"
                      onClick={() => { setOrderId(order.id); setCustomerId(order.customerId); setIsOrderOpen(true); }}
                      >
                      <div                        
                        className="flex flex-col md:flex-row justify-between items-start md:items-center w-full"                        
                      >
                        {/* Columna 1: Order ID */}
                        <div className="flex-1 pr-4">
                          <p className="font-bold">{t("UserProfile.Orders.Id")}</p>
                          <p>{order.id}</p>
                        </div>

                        {/* Columna 2: Order Date */}
                        <div className="flex-1 pr-4">
                          <p className="font-bold">{t("UserProfile.Orders.Date")}</p>
                          <p>{new Date(order.orderDate).toLocaleDateString(`${i18n.language === languages.es.label ? `${i18n.language}-MX` : `${i18n.language}-US`}`)}</p>
                        </div>

                        {/* Columna 3: Total */}
                        <div className="flex-1 pr-4">
                          <p className="font-bold">{t("UserProfile.Orders.Total")} {`(${currency.value})`}</p>
                          <p>${c(order.totalAmount)}</p>
                        </div>

                        {/* Columna 4: Status */}
                        <div className="flex-1 pr-4 md:px-4">
                          <p className="font-bold">{t("UserProfile.Orders.Status")}</p>
                          <p>{t(`General.Statuses.${order.orderStatus}`)}</p>
                        </div>

                      </div>
                      {/* Flecha para detalles */}
                      <div className="flex justify-center items-center h-full pl-4">
                        <ChevronRight className="text-custom-500" />
                      </div>
                    </div>
                  ))
                    :
                    <OrderHistory customerId={customerId} orderId={orderId} onClose={handleOrderHistoryOnClose} />
                  }
                </>
            }

          </div>
        )}
      </div>
    </div>
  );
};

export default withTranslation(withAuth(withMessages(withNotifications(withCurrency(UserProfile)))));
